import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatPaginator } from '@angular/material/paginator';
import { MatSelect } from '@angular/material/select';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { NgxPermissionsService } from 'ngx-permissions';
import { fromEvent, merge, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, take, takeWhile, tap } from 'rxjs/operators';
import { AppState } from '../../../../../core/reducers';
import { StringUtilsService } from '../../../../../core/services/string-utils.service';
import { LayoutUtilsService, MessageType, QueryParamsModel } from '../../../../../core/_base/crud';
import { Category, EntityType } from '../../models';
import { NotificationLevel } from '../../enums';
import { DocumentService } from '../../services';
import { RequestCategoryPage } from '../../actions';
import { CategoryDataSource } from '../../data-sources';
import { selectCategoryActionLoading, selectCategoryError } from '../../selectors';
import { CreateCategoryComponent } from '../create-category/create-category.component';
import { SharingDocConfigComponent } from '../sharing-doc-config/sharing-doc-config.component';
import { currentUser } from '../../../../../core/auth';

@Component({
  selector: 'kt-category-list',
  templateUrl: './category-list.component.html',
  styleUrls: ['./category-list.component.scss'],
})
export class CategoryListComponent implements OnInit, OnDestroy {
  private subscriptions = new Subscription();
  dataSource: CategoryDataSource;
  entityTypes: EntityType[];
  isSuperAdmin: boolean;
  NotificationLevels = Object.values(NotificationLevel);
  displayedCols = [
    'id',
    'name',
    'entityTypes',
    'notificationLevel',
    'createdBySuperAdmin',
    'mobileVisible',
    'actions',
  ];
  searchBarCols = [
    'searchId',
    'searchName',
    'searchEntityTypes',
    'searchNotificationLevel',
    'searchOrigin',
    'searchMobileVisible',
    'searchActions',
  ];
  showSearchBar = false;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;
  @ViewChild('searchIdInput') searchIdInput: ElementRef;
  @ViewChild('searchNameInput') searchNameInput: ElementRef;
  @ViewChild('searchEntityTypesSelect') searchEntityTypesSelect: MatSelect;
  @ViewChild('searchNotificationLevelSelect') searchNotificationLevelSelect: MatSelect;
  @ViewChild('searchOriginSelect') searchOriginSelect: MatSelect;
  @ViewChild('searchMobileVisibleSelect') searchMobileVisibleSelect: MatSelect;
  originOptions = [
    { id: 1, label: 'All', value: '' },
    { id: 2, label: 'Global', value: 'true' },
    { id: 3, label: 'Company', value: 'false' },
  ];
  constructor(
    public stringUtils: StringUtilsService,
    private dialog: MatDialog,
    private store: Store<AppState>,
    private translate: TranslateService,
    private layoutUtils: LayoutUtilsService,
    private permissionsService: NgxPermissionsService,
    private documentService: DocumentService,
  ) {}

  ngOnInit(): void {
    this.store.select(currentUser).subscribe((user) => {
      this.isSuperAdmin = user.userType === 'Super Admin';
      if (this.isSuperAdmin) {
        this.displayedCols = [
          'id',
          'name',
          'entityTypes',
          'notificationLevel',
          'mobileVisible',
          'actions',
        ];
        this.searchBarCols = [
          'searchId',
          'searchName',
          'searchEntityTypes',
          'searchNotificationLevel',
          'searchMobileVisible',
          'searchActions',
        ];
      }
    });
    this.documentService
      .getEntityTypes()
      .pipe(take(1))
      .subscribe(({ data }) => (this.entityTypes = data));

    this.dataSource = new CategoryDataSource(this.store, this.paginator);

    this.subscriptions.add(this.dataSource.entitySubject.subscribe());

    this.subscriptions.add(
      this.sort.sortChange.subscribe((res) => {
        this.paginator.pageIndex = 0;

        if (!res.direction) {
          this.sort.direction = '';
          this.sort.active = '';
        }
      }),
    );

    this.subscriptions.add(
      merge(this.sort.sortChange, this.paginator.page)
        .pipe(tap(() => this.loadCategoryList()))
        .subscribe(),
    );

    this.subscriptions.add(
      fromEvent(this.searchInput.nativeElement, 'keyup')
        .pipe(
          debounceTime(500),
          distinctUntilChanged(),
          tap(() => this.reloadCategoryList()),
        )
        .subscribe(),
    );

    setTimeout(() => this.loadCategoryList());
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  getFilterConfig() {
    const filter: any = {};

    if (this.showSearchBar) {
      filter.id = this.searchIdInput?.nativeElement?.value;
      filter.name = this.searchNameInput?.nativeElement?.value;
      filter.entityTypes = this.searchEntityTypesSelect?.value;
      filter.notificationLevel = this.searchNotificationLevelSelect?.value;
      filter.origin = this.searchOriginSelect?.value;
      filter.mobileVisible = this.searchMobileVisibleSelect?.value;
    } else {
      filter.searchTerm = this.searchInput.nativeElement.value || '';
    }

    return filter;
  }

  loadCategoryList() {
    const query = new QueryParamsModel(
      this.getFilterConfig(),
      this.sort.direction || 'desc',
      this.sort.active || 'id',
      this.paginator.pageIndex,
      this.paginator.pageSize,
    );

    this.store.dispatch(new RequestCategoryPage({ query }));
  }

  displayMessage(message: string, messageType: MessageType): void {
    this.store
      .pipe(
        select(selectCategoryActionLoading),
        takeWhile((isLoading) => isLoading === true),
        finalize(() => {
          this.store.pipe(select(selectCategoryError), take(1)).subscribe((err) => {
            if (err) {
              const status = err.status;

              if (status === 401 || status === 403 || status === 404) return;

              message = this.translate.instant('GLOBAL.UNSPECIFIED_ERROR');
              return this.layoutUtils.showActionNotification(
                message,
                messageType,
                3000,
                false,
                false,
              );
            }

            this.layoutUtils.showActionNotification(message, messageType, 3000, false, false);
            setTimeout(() => this.loadCategoryList(), 500);
          });
        }),
      )
      .subscribe();
  }

  getStatusClass(status: string): string {
    return status === 'true' ? 'success' : status === 'false' ? 'danger' : 'warning';
  }

  getStatusLabel(status: string): string {
    return status === 'true'
      ? this.translate.instant('GLOBAL.ACTIVE')
      : status === 'false'
      ? this.translate.instant('GLOBAL.INACTIVE')
      : this.translate.instant('GLOBAL.ALL');
  }

  manageSharingConfigs(event: Event): void {
    event.stopPropagation();

    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.closeOnNavigation = true;
    dialogConfig.width = '600px';

    const dialogRef = this.dialog.open(SharingDocConfigComponent, dialogConfig);

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) return;

        this.displayMessage(
          this.translate.instant('DOCS_MODULE.CATEGORY.UPDATE_SHARING_CONFIGS_MESSAGE'),
          MessageType.Update,
        );
      });
  }

  addCategory(event: Event): void {
    event.stopPropagation();

    const category = new Category();
    this.updateCategory(null, category);
  }

  updateCategory(event: Event, category: Category): void {
    event?.stopPropagation();

    const message = this.translate.instant(
      `DOCS_MODULE.CATEGORY.${category.id ? 'UPDATE_MESSAGE' : 'CREATE_MESSAGE'}`,
    );
    const messageType = category.id ? MessageType.Update : MessageType.Create;
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.closeOnNavigation = true;
    dialogConfig.width = '600px';
    dialogConfig.data = { category, viewMode: false };

    const dialogRef = this.dialog.open(CreateCategoryComponent, dialogConfig);

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) return;

        this.displayMessage(message, messageType);
      });
  }

  canViewCategory(): boolean {
    return !!this.permissionsService.getPermission('canViewDocumentCategory');
  }

  viewCategory(event: Event, category: Category): void {
    event.stopPropagation();

    if (!this.canViewCategory()) return;

    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.closeOnNavigation = true;
    dialogConfig.width = '600px';
    dialogConfig.data = { category, viewMode: true };

    this.dialog.open(CreateCategoryComponent, dialogConfig);
  }

  reloadCategoryList(): void {
    this.paginator.pageIndex = 0;

    this.loadCategoryList();
  }

  resetSearchBar(event?: Event): void {
    event?.stopPropagation();

    this.searchIdInput.nativeElement.value = '';
    this.searchNameInput.nativeElement.value = '';
    this.searchEntityTypesSelect.value = [];
    this.searchNotificationLevelSelect.value = '';
    if (this.searchOriginSelect) this.searchOriginSelect.value = '';
    this.searchMobileVisibleSelect.value = '';
  }

  resetInput(event: Event, inputEl: HTMLInputElement, reload = false): void {
    event.stopPropagation();

    inputEl.value = '';

    if (reload) this.reloadCategoryList();
  }

  toggleSearchBar(event: Event): void {
    event.stopPropagation();

    this.resetSearchBar();
    this.showSearchBar = !this.showSearchBar;

    if (this.showSearchBar) {
      this.searchInput.nativeElement.value = '';
      this.searchInput.nativeElement.disabled = true;
      setTimeout(() => this.searchIdInput.nativeElement.focus());
    } else {
      this.searchInput.nativeElement.disabled = false;
      this.searchInput.nativeElement.focus();
    }

    this.reloadCategoryList();
  }

  handleSearchByField(event: Event): void {
    event.stopPropagation();

    this.reloadCategoryList();
  }
}
